import React, { useCallback, useEffect, useRef, useState } from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import Close from '@material-ui/icons/Close';
import CustomInput from 'components/CustomInput/CustomInput';
import styles from 'assets/jss/material-dashboard-pro-react/components/sidebarSearchStyle';
import { makeStyles } from '@material-ui/core/styles';
import typography from 'assets/jss/material-dashboard-pro-react/components/typographyStyle';
import classNames from 'classnames';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import SingleSuggestion from './subcomponents/SingleSuggestion';
import {
	DEBOUNCE_TIME,
	MENU_ITEM_HEIGHT,
	DROPDOWN_MAX_HEIGHT,
	MIN_SEARCH_LENGTH,
	NEW_DROPDOWN_MAX_HEIGHT,
} from './const';
import { translate } from 'components/Translation';
import { SearchService } from 'services/search/search';
import { finalize } from 'rxjs/operators';
import { SingleSearchNumber } from 'utils/api/models/search/singleSearchNumber';
import { useHistory } from 'react-router';
import { BodyS } from 'components/Typography/Typography';
import { numberDetailsPath as calculatedNumberDetailsPath } from 'utils/paths/numberDetailsPath';
import { billingAccountDetailsPath as calculatedBillingAccountDetailsPath } from 'utils/paths/billingAccountDetailsPath';
import { SingleSearchBillingAccount } from 'utils/api/models/search/singleSearchBillingAccount';
import { AAMALI_PLUS_ACCOUNT_CATEGORY } from 'views/Numbers/constants';
import { AccountType } from 'utils/api/models/accounts/accountType';
import { Box } from '@material-ui/core';
import loaderSVG from 'assets/img/icons/loader.svg';
import warningSVG from 'assets/img/icons/warning.svg';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';
import SearchIcon from 'assets/img/icons/search.svg';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { getLanguage } from 'utils/language/language';
import { useSelector } from 'react-redux';
import { upIdSelector } from 'redux/modules/session/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);
// @ts-ignore
const useTypography = makeStyles(typography);

const Search = () => {
	const accId = useSelector(upIdSelector);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [suggestions, setSuggestions] = useState<types.search.ISingleSuggestion[]>([]);
	const [activeSuggestion, setActiveSuggestion] = useState<number>(0);
	const isNewDashBoardEnabled = useNewTheme();
	const [inputWrapperClasses, setInputWrapperClasses] = useState('');
	const [searchFocus, setSearchFocus] = useState(false);

	const classes = useStyles({
		dropdownMaxHeight: isNewDashBoardEnabled ? NEW_DROPDOWN_MAX_HEIGHT : DROPDOWN_MAX_HEIGHT,
		menuItemHeight: MENU_ITEM_HEIGHT,
		newDashBoardEnabled: isNewDashBoardEnabled,
		searchFocus: searchFocus,
	});
	const typography = useTypography();
	const inputCustomClasses = classNames({
		[typography.bodyS]: !isNewDashBoardEnabled,
		[classes.input]: true,
	});
	const showDropdown: boolean = searchInputValue.length >= MIN_SEARCH_LENGTH && isOpen;
	const dropdown = useRef<HTMLDivElement>(null);
	const history = useHistory();

	// Update the class names based on props
	useEffect(() => {
		const updatedClasses = classNames({
			[classes.inputWrapper]: true,
			[classes.newInputWrapper]: isNewDashBoardEnabled,
			[classes.show]: isNewDashBoardEnabled && showDropdown,
			[classes.searchFocus]: isNewDashBoardEnabled && searchFocus,
		});

		setInputWrapperClasses(updatedClasses);
	}, [isNewDashBoardEnabled, showDropdown, searchFocus, classes]);

	const dropdownWrapperClasses = classNames({
		[classes.dropdownWrapper]: true,
		[classes.newDropdownWrapper]: isNewDashBoardEnabled,
	});

	const clear = (): void => {
		setSearchInputValue('');
		setSuggestions([]);
	};

	const sleep = (milliseconds: number) => {
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	};

	const goTo = (path: string) => {
		history.push(path);
	};

	const userDetailsPath = useCallback((userId: string): string => {
		return `/dashboard/users/${userId}`;
	}, []);

	const numberDetailsPath = useCallback((number: SingleSearchNumber): string => {
		return calculatedNumberDetailsPath(
			number.accountNum,
			number.bAccountId,
			number.accountType,
			number.billingType,
			number.upAccountId,
			number.accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY && number.accountType === AccountType.INTERNET,
			AAMALI_PLUS_ACCOUNT_CATEGORY
		);
	}, []);

	const billingAccountDetailsPath = (billingAcc: SingleSearchBillingAccount): string => {
		return calculatedBillingAccountDetailsPath(billingAcc.billingId, billingAcc.accountId);
	};

	const showDetails = (index: number) => {
		if (index >= 0 && index < suggestions.length) {
			const selectedSuggestion = suggestions[index];
			let path = '';
			if (selectedSuggestion.type === 'USER') {
				path = userDetailsPath(selectedSuggestion.suggestion.userId);
			} else if (selectedSuggestion.type === 'BILLING_ACCOUNT') {
				path = billingAccountDetailsPath(selectedSuggestion.suggestion);
			} else {
				path = numberDetailsPath(selectedSuggestion.suggestion);
			}
			goTo(path);
		}
	};

	const onBlur = (event: any) => {
		if (!event?.currentTarget?.contains(event?.relatedTarget)) {
			setIsOpen(false);
		}
		setSearchFocus(false);
	};

	const onInputClick = () => {
		if (suggestions.length > 0) {
			setIsOpen(true);
		}
		setSearchFocus(true);
	};

	useEffect(() => {
		if (dropdown.current && activeSuggestion >= 0) {
			const scrollTop = dropdown.current.scrollTop;
			if (activeSuggestion * MENU_ITEM_HEIGHT < scrollTop) {
				dropdown.current.scrollTop = activeSuggestion * MENU_ITEM_HEIGHT;
			} else if ((activeSuggestion + 1) * MENU_ITEM_HEIGHT > scrollTop + DROPDOWN_MAX_HEIGHT) {
				dropdown.current.scrollTop = (activeSuggestion + 1) * MENU_ITEM_HEIGHT - DROPDOWN_MAX_HEIGHT;
			}
		}
	}, [activeSuggestion]);

	const arrowDown = () => {
		if (activeSuggestion < suggestions.length - 1) {
			setActiveSuggestion((index) => index + 1);
		}
	};

	const arrowUp = () => {
		if (activeSuggestion > 0) {
			setActiveSuggestion((index) => index - 1);
		}
	};

	const handleOnKeyDown = (event: KeyboardEvent) => {
		const allowedKeys = ['Backspace', 'ArrowDown', 'ArrowUp', 'Enter'];

		const regex = /^([A-Za-z0-9\s_-]+)$/;
		if (!regex.test(event.key) && !allowedKeys.includes(event.key)) {
			event.preventDefault();
		}
		switch (event.key) {
			case 'ArrowDown':
				arrowDown();
				break;
			case 'ArrowUp':
				arrowUp();
				break;
			case 'Enter':
				showDetails(activeSuggestion);
				break;
			default:
				return;
		}
	};

	const fetchData = useCallback((query: string) => {
		setIsOpen(true);
		SearchService.searchNumbersAndUsersRestPost(query)
			.pipe(
				finalize(() => {
					setActiveSuggestion(-1);
					setLoading(false);
				})
			)
			.subscribe(
				(searchResults) => {
					const users: types.search.IUserSuggestion[] = searchResults.users.map((user) => ({
						type: 'USER',
						suggestion: user,
					}));
					const numbers: types.search.INumberSuggestion[] = searchResults.numbers.map((number) => ({
						type: 'NUMBER',
						suggestion: number,
					}));
					const billingAccounts: types.search.IBillingAccountSuggestion[] = searchResults.billingAccounts.map(
						(billingAcc) => ({
							type: 'BILLING_ACCOUNT',
							suggestion: billingAcc,
						})
					);
					const result = [...users, ...numbers, ...billingAccounts];
					setDataLayer('view_search_results', getLanguage(), null, accId, null, {
						search_term: searchInputValue,
						search_count: result.length || 0,
					});
					setSuggestions(result);
				},
				() => {
					setSuggestions([]);
				}
			);
	}, []);

	useEffect(() => {
		let isMounted = true;
		setLoading(true);
		if (searchInputValue.length >= MIN_SEARCH_LENGTH) {
			const load = async () => {
				await sleep(DEBOUNCE_TIME);
				if (isMounted) {
					fetchData(searchInputValue);
				}
			};
			load();
		} else {
			setIsOpen(false);
			setSuggestions([]);
		}
		return () => {
			isMounted = false;
		};
	}, [searchInputValue, fetchData]);
	return (
		<section role="search" className={classes.searchWrapper} tabIndex={0} onBlur={onBlur}>
			<div className={inputWrapperClasses}>
				<CustomInput
					id="search-number-and-user-input"
					formControlProps={{
						fullWidth: true,
					}}
					inputCustomClasses={inputCustomClasses}
					inputProps={{
						value: searchInputValue,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
							event.preventDefault();
							setSearchInputValue(event.target.value);
						},
						onClick: onInputClick,
						onKeyDown: handleOnKeyDown,
						startAdornment: isNewDashBoardEnabled ? null : (
							<InputAdornment position="start" className={classes.searchIcon}>
								<img src={SearchIcon} alt="" role="presentation" />
							</InputAdornment>
						),
						disableUnderline: true,
						placeholder: translate('sidebar.search.placeholder'),
						autoComplete: 'off',
					}}
				/>
				<ConditionalRender
					show={searchInputValue.length > 0}
					onTrue={() => (
						<div className={classes.clearIconWrapper} onClick={clear}>
							<Close />
						</div>
					)}
				/>
				<ConditionalRender
					show={isNewDashBoardEnabled && searchInputValue.length === 0}
					onTrue={() => (
						<div className={classes.clearIconWrapper}>
							<img src={SearchIcon} alt="" role="presentation" />
						</div>
					)}
				/>
			</div>
			<ConditionalRender
				show={showDropdown}
				onTrue={() => (
					<div className={dropdownWrapperClasses} ref={dropdown} id="search-number-and-user-dropdown">
						<ConditionalRender
							show={loading}
							onTrue={() =>
								isNewDashBoardEnabled ? (
									<Box display="flex" alignItems="center" justifyContent="center" minHeight="100px">
										<img src={loaderSVG} width="48px" alt={translate('loading')} />
									</Box>
								) : (
									<CustomCircularProgress
										text={translate('loading')}
										loading={loading}
										transparent
										negative
									/>
								)
							}
							onFalse={() => (
								<div>
									{suggestions.map((singleSuggestion, index) => (
										<div
											key={index}
											onMouseOver={() => setActiveSuggestion(index)}
											onMouseOut={() => setActiveSuggestion(-1)}
										>
											<SingleSuggestion
												suggestion={singleSuggestion}
												query={searchInputValue}
												active={index === activeSuggestion}
												userDetailsPath={userDetailsPath}
												numberDetailsPath={numberDetailsPath}
												billingAccountDetailsPath={billingAccountDetailsPath}
												isNewDashBoardEnabled={isNewDashBoardEnabled}
												accId={accId}
												searchInputValue={searchInputValue}
											/>
										</div>
									))}
									<ConditionalRender
										show={suggestions.length === 0}
										onTrue={() =>
											isNewDashBoardEnabled ? (
												<div className={classes.noResultsNew}>
													<img src={warningSVG} width="48px" alt={translate('no.results')} />
													<BodyS color="primary-light" disableMargin inline>
														{translate('no.results')}
													</BodyS>
												</div>
											) : (
												<div className={classes.noResults}>
													<BodyS color="primary-dark" disableMargin inline>
														{translate('no.results')}
													</BodyS>
												</div>
											)
										}
									/>
								</div>
							)}
						/>
					</div>
				)}
			/>
		</section>
	);
};

export default Search;
